import { z } from 'zod';
import { SelfAssessmentDimensionSchema } from './self-assessment.js';
import { ChunkRefSchema } from './source-ref.js';
import { ItemTagsSchema, coerceItemTagsV3ToV4 } from './item-tags.js';
import { ConceptOmissionSchema } from './concept-omission.js';
// Session 19AE: full .describe() for LLM clarity. Essays don't carry
// cognitiveLevel — by their nature evaluate-tier (Bloom).
//
// Session 19AO: selfAssessmentRubric added (unified scale per LLM_NATIVE §10
// + 陛下 19AO "用户自评通用于 essay / 论述 / 推理"). Optional — older essay
// data (pre-19AO) renders without rubric panel; new ingest produces it.
//
// 19BH Day 2 Step 2.4 · v4 ItemTags base:
//   - caseIds (multi-chapter array) is the v4 canonical form. v3 had both
//     singular caseId (required) and plural caseIds (.min(2).optional() for
//     cross-chapter). v4 unifies: ItemTagsSchema provides caseIds (required
//     array, may have 1 or 2+ elements). Singular caseId becomes deprecated
//     optional alias for engine back-compat.
export const EssaySchema = z.preprocess(coerceItemTagsV3ToV4, ItemTagsSchema.extend({
    id: z.string().regex(/^essay[a-z0-9_]+$/, 'essay id must start with "essay" (lowercase, snake_case)')
        .describe('Unique snake_case slug starting with `essay` (e.g. `essay_argentina_01`).'),
    /** @deprecated v3 alias of caseIds[0]. Day 5 finalize sets for engine back-compat. */
    caseId: z.string().nullable().optional()
        .describe('@deprecated v3 alias of caseIds[0]. Use caseIds[] · v4 unified single/multi-chapter under array form.'),
    /** @deprecated v3 alias of topicIds[0]. */
    topicId: z.string().nullable().optional()
        .describe('@deprecated v3 alias of topicIds[0].'),
    subtopicId: z.string().nullable().optional()
        .describe('Subtopic id with topic prefix. null/omit if not applicable.'),
    prompt: z.string().min(30)
        .describe('The essay question. ≥30 chars. Phrased as a directive (Discuss / Compare / Evaluate / Argue). Single clear question — no compound prompts.'),
    keyPoints: z.array(z.string().min(10)).min(3).max(8)
        .describe('Required factual / conceptual elements a strong answer must include. 3-8 points (was min 1 — bumped Session 19AO to enforce essay rubric depth). Each ≥10, ≤140 chars. Used by AI grader as rubric anchors.'),
    scaffold: z.array(z.string().min(8)).min(3).max(8)
        .describe('Suggested structure as ordered scaffold steps. 3-8 items (was min 1 — Session 19AO). Each step is one section/paragraph the student should write (e.g. "Define the framework", "Apply to case", "Counter-argument", "Conclusion"). Markdown allowed.'),
    modelAnswer: z.string().min(200)
        .describe('Full markdown model answer. ≥200 chars (real essay-length · not "Answer here."). Length matches wordTarget. Demonstrates ALL keyPoints used. Use ## section headings matching scaffold steps. Inline math, block quotes, examples encouraged. Constraint Session 19AO.'),
    wordTarget: z.number().int().positive()
        .describe('Target word count for the model answer (typical exam essay: 400-1200 depending on time allocation).'),
    selfAssessmentRubric: z.array(SelfAssessmentDimensionSchema).min(2).max(6).nullish()
        .describe('Session 19AO: 2-6 rubric dimensions for the user to self-grade their essay against (e.g. content / structure / evidence / language). Optional for backward-compat with pre-19AO essay data; new ingest should produce it.'),
    /** @deprecated v3 alias of conceptIds[0]. */
    conceptTag: z.string().min(1).max(80).nullish()
        .describe('@deprecated v3 alias of conceptIds[0]. New code should read conceptIds[].'),
    sourceRef: ChunkRefSchema.nullish()
        .describe('19BG+2 Phase 5 (DEBT-AW-SOURCEREF): citation anchor for the essay\'s primary source. Optional for back-compat.'),
    omittedConcepts: z.array(ConceptOmissionSchema).nullish()
        .describe('K18 soft escape · concepts the LLM declared materially insufficient for this stage.'),
}).passthrough());
export const EssaysSchema = z.array(EssaySchema);
/**
 * AI-scored essay feedback — Session 19N interface RESERVATION.
 * Server-side scorer NOT YET IMPLEMENTED; structure locked so future writes
 * don't break legacy persisted drafts.
 */
export const EssayAIScoreSchema = z.object({
    score: z.number().min(0).max(100)
        .describe('Overall 0-100 score.'),
    rubric: z.array(z.object({
        dimension: z.string().min(1)
            .describe('Rubric dimension (e.g. "Argument structure", "Use of evidence").'),
        score: z.number().min(0).max(100)
            .describe('Per-dimension 0-100 score.'),
        comment: z.string()
            .describe('Specific feedback for this dimension.'),
    })).default([])
        .describe('Per-dimension breakdown. Empty array allowed; UI defends.'),
    feedback: z.string()
        .describe('Overall feedback (markdown allowed).'),
    scoredAt: z.string().datetime()
        .describe('ISO datetime when scored.'),
    modelVersion: z.string()
        .describe('Provider/model identity for forensic value (e.g. "deepseek:deepseek-v4-pro").'),
});
export const EssayDraftSchema = z.object({
    essayId: z.string(),
    content: z.string(),
    updatedAt: z.string().datetime(),
    wordCount: z.number().int().nonnegative(),
    aiScore: EssayAIScoreSchema.nullable(),
});
//# sourceMappingURL=essay.js.map